import { Fragment, type ReactNode } from 'react';
import { LEGAL_DOCS, getLegalDoc, type LegalBlock, type LegalDoc, type LegalDocId } from '@/data/legal';
import { legalHash } from '@/lib/legalRoute';

/**
 * Standalone legal document page (29 JUN 2026). Rendered by App.tsx's LegalGate
 * above the auth gate, so it works signed-in and signed-out and is deep-linkable
 * (#/terms · #/privacy · #/cookies · #/consent). Content comes from data/legal.ts.
 *
 * Self-contained chrome (brand + back) — does NOT use NavShell, ProgressProvider
 * or any authed context, so it can render for an anonymous visitor.
 */

export interface LegalScreenProps {
  docId: LegalDocId;
  onBack: () => void;
}

/** Highlight [PLACEHOLDER] tokens so the team can spot what to fill in. */
function withPlaceholders(text: string): ReactNode {
  const parts = text.split(/(\[[^\]]+\])/g);
  return parts.map((part, i) =>
    /^\[[^\]]+\]$/.test(part) ? (
      <mark key={i} className="legal-ph">
        {part}
      </mark>
    ) : (
      <Fragment key={i}>{part}</Fragment>
    ),
  );
}

function Block({ block }: { block: LegalBlock }) {
  switch (block.type) {
    case 'p':
      return <p className="legal-p">{withPlaceholders(block.text)}</p>;
    case 'ul':
      return (
        <ul className="legal-ul">
          {block.items.map((it, i) => (
            <li key={i}>{withPlaceholders(it)}</li>
          ))}
        </ul>
      );
    case 'ol':
      return (
        <ol className="legal-ol">
          {block.items.map((it, i) => (
            <li key={i}>{withPlaceholders(it)}</li>
          ))}
        </ol>
      );
    case 'note':
      return <p className="legal-note">{withPlaceholders(block.text)}</p>;
    case 'table':
      return (
        <div className="legal-table-wrap">
          <table className="legal-table">
            <thead>
              <tr>
                {block.head.map((h, i) => (
                  <th key={i}>{withPlaceholders(h)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, ri) => (
                <tr key={ri}>
                  {row.map((cell, ci) => (
                    <td key={ci}>{withPlaceholders(cell)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
  }
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  try {
    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return iso;
  }
}

export function LegalScreen({ docId, onBack }: LegalScreenProps) {
  const doc: LegalDoc | undefined = getLegalDoc(docId);

  if (!doc) {
    return (
      <div className="legal-page">
        <div className="legal-shell">
          <p className="legal-p">Документ не найден.</p>
          <button type="button" className="legal-back" onClick={onBack}>
            ← Назад
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="legal-page">
      <header className="legal-topbar">
        <a className="legal-brand" href="/" aria-label="QA Studio">
          <span className="legal-brand__dot">QA</span>
          <span>Studio</span>
        </a>
        <button type="button" className="legal-back" onClick={onBack}>
          ← Назад
        </button>
      </header>

      <main className="legal-shell" id="main">
        <article className="legal-doc">
          <h1 className="legal-title">{doc.title}</h1>
          <p className="legal-meta">
            Редакция {doc.version} · обновлено {formatDate(doc.updated)}
          </p>
          {doc.summary && <p className="legal-summary">{doc.summary}</p>}

          <nav className="legal-toc" aria-label="Содержание">
            <ol>
              {doc.sections.map((s) => (
                <li key={s.id}>
                  <a href={`#/${doc.slug}`} onClick={(e) => {
                    e.preventDefault();
                    const el = document.getElementById(`sec-${s.id}`);
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}>
                    {s.heading}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          {doc.sections.map((section) => (
            <section key={section.id} id={`sec-${section.id}`} className="legal-section">
              <h2 className="legal-h2">{section.heading}</h2>
              {section.blocks.map((b, i) => (
                <Block key={i} block={b} />
              ))}
            </section>
          ))}
        </article>

        <nav className="legal-docnav" aria-label="Другие документы">
          <span className="legal-docnav__label">Другие документы:</span>
          {LEGAL_DOCS.filter((d) => d.id !== doc.id).map((d) => (
            <a key={d.id} href={legalHash(d.id)} className="legal-docnav__link">
              {d.shortTitle}
            </a>
          ))}
        </nav>
      </main>
    </div>
  );
}
